import { useMemo } from 'react';
import { EUserType } from 'data/types/user.types';
import { TEventDetailsDataUI } from 'data/types/events.types';

export type TEventAccessControl = {
  canEdit: boolean;
  canView: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isElcPm: boolean;
  isSiteCoordinator: boolean;
  isCafeCoordinator: boolean;
  isOwner: boolean;
  isAssignedCoordinator: boolean;
};

type TUseEventAccessControlParams = {
  userTypes?: EUserType[];
  userId?: string;
  eventData?: TEventDetailsDataUI;
};

/**
 * Hook to determine event access control based on user role and ownership
 *
 * Access Rules:
 * - ADMIN (READY_ADMIN, SUPER_ADMIN, ELC_LEAD): Full access (edit + view)
 * - ELC_PM: Full access (edit + view)
 * - SITE_COORDINATOR:
 *   - If assigned to the event: Full editing capabilities (edit + view)
 *   - If not assigned: View only
 * - CAFE_COORDINATOR:
 *   - If assigned to the event: Full editing capabilities (edit + view)
 *   - If not assigned: View only
 * - Others: View only
 */
export const useEventAccessControl = ({
  userTypes = [],
  userId,
  eventData,
}: TUseEventAccessControlParams): TEventAccessControl => {
  return useMemo(() => {
    const isSuperAdmin = userTypes.includes(EUserType.SUPER_ADMIN);
    const isAdmin =
      userTypes.includes(EUserType.READY_ADMIN) ||
      isSuperAdmin ||
      userTypes.includes(EUserType.ELC_LEAD);
    const isElcPm = userTypes.includes(EUserType.ELC_PM);
    const isSiteCoordinator = userTypes.includes(EUserType.SITE_COORDINATOR);
    const isCafeCoordinator = userTypes.includes(EUserType.CAFE_COORDINATOR);
    const isOwner = eventData?.isOwner ?? false;

    // Check if user is assigned as a coordinator to this event. Events carry
    // two separate coordinator relations — `siteCoordinators` (legacy) and
    // `eventCoordinators` (current). An assignment in either grants edit.
    const isAssignedCoordinator =
      !!userId &&
      [
        ...(eventData?.siteCoordinators ?? []),
        ...(eventData?.eventCoordinators ?? []),
      ].some(coordinator => coordinator.id === userId);

    // Admin has full access
    if (isAdmin) {
      return {
        canEdit: true,
        canView: true,
        isAdmin,
        isSuperAdmin,
        isElcPm,
        isSiteCoordinator,
        isCafeCoordinator,
        isOwner,
        isAssignedCoordinator,
      };
    }

    // ELC_PM: always has full editing capabilities on events
    if (isElcPm) {
      return {
        canEdit: true,
        canView: true,
        isAdmin,
        isSuperAdmin,
        isElcPm,
        isSiteCoordinator,
        isCafeCoordinator,
        isOwner,
        isAssignedCoordinator,
      };
    }

    // SITE_COORDINATOR: edit if assigned to this event, otherwise view only
    if (isSiteCoordinator) {
      return {
        canEdit: isAssignedCoordinator,
        canView: true,
        isAdmin,
        isSuperAdmin,
        isElcPm,
        isSiteCoordinator,
        isCafeCoordinator,
        isOwner,
        isAssignedCoordinator,
      };
    }

    // CAFE_COORDINATOR: edit if assigned to this event, otherwise view only
    if (isCafeCoordinator) {
      return {
        canEdit: isAssignedCoordinator,
        canView: true,
        isAdmin,
        isSuperAdmin,
        isElcPm,
        isSiteCoordinator,
        isCafeCoordinator,
        isOwner,
        isAssignedCoordinator,
      };
    }

    // Default to view-only for other users
    return {
      canEdit: false,
      canView: true,
      isAdmin,
      isSuperAdmin,
      isElcPm,
      isSiteCoordinator,
      isCafeCoordinator,
      isOwner,
      isAssignedCoordinator,
    };
  }, [
    userTypes,
    userId,
    eventData?.isOwner,
    eventData?.siteCoordinators,
    eventData?.eventCoordinators,
  ]);
};
